import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, Switch, Row, Col, Button, message } from 'antd';
import { UserOutlined, MailOutlined, TeamOutlined } from '@ant-design/icons';
import useContactsStore from '../../stores/useContactsStore';
import { validateContactMobile, updateContactDetails } from '../../reducers/contact-detail/resources';
import { createGroupV3, addContactToGroups } from '../../reducers/group/resources';
import PhoneInputUS from '../common/PhoneInputUS';
import format from '../../utils/format';

const { Option } = Select;

function normalizeMobileValue(raw) {
    if (typeof raw === 'object' && raw !== null) {
        const { countryCode, areaCode, phoneNumber } = raw;
        if (!areaCode && !phoneNumber) return '';
        return `+${countryCode || 1}${areaCode || ''}${phoneNumber || ''}`;
    }
    return format.mobileCleanup(raw || '');
}

const CreateContactModal = () => {
    const {
        isContactModalOpen,
        setIsContactModalOpen,
        createContact,
        contactModalLoading,
        availableGroups,
        fetchContacts
    } = useContactsStore();
    const [form] = Form.useForm();
    const [isNewGroup, setIsNewGroup] = useState(false);
    const [mobileStatus, setMobileStatus] = useState({ status: '', help: null });
    const [validating, setValidating] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [duplicateContact, setDuplicateContact] = useState(null);
    const [duplicateMobile, setDuplicateMobile] = useState(null);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);

    useEffect(() => {
        if (!isContactModalOpen) {
            form.resetFields();
            setIsNewGroup(false);
            setMobileStatus({ status: '', help: null });
            setDuplicateContact(null);
            setDuplicateMobile(null);
            setShowDuplicateModal(false);
            setUpdating(false);
        }
    }, [isContactModalOpen]);

    const buildDuplicateHelp = (existingContact) => (
        <span>
            This number is already assigned to{' '}
            <strong>{existingContact.name}</strong>.
        </span>
    );

    const applyGroupAssignment = async (contactId, values) => {
        if (isNewGroup && values.groupName) {
            await createGroupV3({
                name: values.groupName,
                privacy: values.isPrivate ? 'private' : 'public',
                contacts: [contactId],
            });
        } else if (!isNewGroup && values.selectedGroup) {
            await addContactToGroups(contactId, [values.selectedGroup]);
        }
    };

    const handleUpdateExisting = async () => {
        if (!duplicateContact) return;
        try {
            const values = await form.validateFields();
            setUpdating(true);
            await updateContactDetails(duplicateContact.id, {
                firstname: values.firstname,
                lastname: values.lastname,
                email: values.email || '',
            });
            await applyGroupAssignment(duplicateContact.id, values);
            message.success(`Updated ${duplicateContact.name}`);
            setShowDuplicateModal(false);
            setIsContactModalOpen(false);
            fetchContacts();
        } catch (error) {
            if (!error.errorFields) {
                console.error('Update existing contact failed:', error);
                message.error('Failed to update existing contact');
            }
        } finally {
            setUpdating(false);
        }
    };

    const submitContact = async (values) => {
        const mobile = normalizeMobileValue(values.mobile);
        await createContact({
            ...values,
            mobile,
            isNewGroup
        });
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            const mobile = normalizeMobileValue(values.mobile);

            if (!mobile) {
                setMobileStatus({ status: 'error', help: 'Please enter a valid mobile number' });
                return;
            }

            // If this same mobile was already flagged as duplicate, re-open the unified
            // decision modal without re-validating.
            if (duplicateContact && duplicateMobile === mobile) {
                setShowDuplicateModal(true);
                return;
            }

            setValidating(true);
            setMobileStatus({ status: 'validating', help: 'Checking number...' });

            const res = await validateContactMobile({ mobile, contactId: null });
            setValidating(false);

            const data = res?.data || {};

            if (!data.isValid) {
                if (data.reason === 'unique' && data.existingContact) {
                    setDuplicateContact(data.existingContact);
                    setDuplicateMobile(mobile);
                    setMobileStatus({
                        status: 'warning',
                        help: buildDuplicateHelp(data.existingContact),
                    });
                    setShowDuplicateModal(true);
                    return;
                }
                setMobileStatus({
                    status: 'error',
                    help: data.message || 'Invalid mobile number',
                });
                return;
            }

            setMobileStatus({ status: 'success', help: null });
            await submitContact(values);
        } catch (error) {
            setValidating(false);
            if (!error.errorFields) {
                console.error('Create contact failed:', error);
            }
        }
    };

    const handleDuplicateCancel = () => {
        setShowDuplicateModal(false);
    };

    const handleCancel = () => {
        setIsContactModalOpen(false);
    };

    const handleMobileChange = () => {
        // Any edit to the mobile field invalidates the previous duplicate check.
        setDuplicateContact(null);
        setDuplicateMobile(null);
        if (mobileStatus.status) {
            setMobileStatus({ status: '', help: null });
        }
    };

    const phoneValidator = (_, value) => {
        if (!value) return Promise.reject('Please enter mobile number');
        if (typeof value === 'object') {
            if (!value.areaCode && !value.phoneNumber) {
                return Promise.reject('Please enter mobile number');
            }
        }
        return Promise.resolve();
    };

    return (
        <>
        <Modal
            title="Add New Contact"
            open={isContactModalOpen}
            onOk={handleOk}
            onCancel={handleCancel}
            confirmLoading={contactModalLoading || validating || updating}
            width={600}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{ isPrivate: false }}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="firstname"
                            label="First Name"
                            rules={[{ required: true, message: 'Please enter first name' }]}
                        >
                            <Input prefix={<UserOutlined />} placeholder="First Name" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="lastname"
                            label="Last Name"
                            rules={[{ required: true, message: 'Please enter last name' }]}
                        >
                            <Input prefix={<UserOutlined />} placeholder="Last Name" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="mobile"
                            label="Mobile Number"
                            rules={[{ validator: phoneValidator }]}
                            validateStatus={mobileStatus.status || undefined}
                            help={mobileStatus.help}
                        >
                            <PhoneInputUS enableSearch onChange={handleMobileChange} disabled={validating} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="email"
                            label="Email"
                            rules={[{ type: 'email', message: 'Please enter valid email' }]}
                        >
                            <Input prefix={<MailOutlined />} placeholder="email@example.com" />
                        </Form.Item>
                    </Col>
                </Row>

                 <Form.Item label="Group Assignment">
                    <div style={{ marginBottom: 8 }}>
                        <Switch 
                            checked={isNewGroup} 
                            onChange={setIsNewGroup} 
                            checkedChildren="New Group" 
                            unCheckedChildren="Existing Group" 
                        />
                    </div>
                </Form.Item>

                {isNewGroup ? (
                    <Row gutter={16}>
                         <Col span={12}>
                            <Form.Item
                                name="groupName"
                                label="New Group Name"
                                rules={[{ required: true, message: 'Please enter group name' }]}
                            >
                                <Input prefix={<TeamOutlined />} placeholder="Group Name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                             <Form.Item
                                name="isPrivate"
                                label="Private Group"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>
                ) : (
                    <Form.Item
                        name="selectedGroup"
                        label="Select Group"
                    >
                        <Select placeholder="Select a group" allowClear>
                            {availableGroups.map(g => (
                                <Option key={g.id} value={g.id}>{g.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}

                <Form.Item
                    name="notes"
                    label="Notes"
                >
                    <Input.TextArea rows={3} placeholder="Add specific notes about this contact..." />
                </Form.Item>
            </Form>
        </Modal>

        <Modal
            title="Duplicate Contact Found"
            open={showDuplicateModal}
            onCancel={handleDuplicateCancel}
            maskClosable={!updating}
            closable={!updating}
            footer={[
                <Button
                    key="cancel"
                    onClick={handleDuplicateCancel}
                    disabled={updating}
                >
                    Cancel
                </Button>,
                <Button
                    key="update"
                    type="primary"
                    onClick={handleUpdateExisting}
                    loading={updating}
                >
                    Update Existing
                </Button>,
            ]}
        >
            <p>
                A contact with this mobile number already exists:{' '}
                <strong>{duplicateContact?.name}</strong>
            </p>
            <p style={{ marginBottom: 4 }}>Choose how to proceed:</p>
            <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                <li><strong>Update Existing</strong> — overwrite {duplicateContact?.name}'s name & email with what you entered{(isNewGroup && form.getFieldValue('groupName')) || (!isNewGroup && form.getFieldValue('selectedGroup')) ? ', and add them to the selected group' : ''}.</li>
                <li><strong>Cancel</strong> — go back and edit the number.</li>
            </ul>
        </Modal>
        </>
    );
};

export default CreateContactModal;
